import type {
  Therapist,
  TherapistDTO,
  TermsAndConditionsDTO,
  TermsAndConditions,
  ReadAccessTherapist,
  ReadAccessTherapistDTO,
  TherapistPreferences,
  TherapistPreferencesDTO,
} from "@akina/domain";
import { decodeHtmlTextFromString, nullableDecodeHtmlTextFromString } from "@akina/utils";
import { mapDateFromApi, type Mapper } from "./";

export const readAccessTherapistMapper: Mapper<ReadAccessTherapist, ReadAccessTherapistDTO> = {
  toDomain: therapistDto => ({
    ...therapistMapper.toDomain(therapistDto),
    noOfPatients: therapistDto.no_of_patients,
    role: therapistDto.role,
    lastActive: therapistDto.last_active ? mapDateFromApi(therapistDto.last_active) : undefined,
  }),
  toDTO: therapistDomain => ({
    ...therapistMapper.toDTO(therapistDomain),
    no_of_patients: therapistDomain.noOfPatients,
    role: therapistDomain.role,
    last_active: therapistDomain.lastActive ? therapistDomain.lastActive.toISOString() : undefined,
  }),
};

export const therapistMapper: Mapper<Therapist, TherapistDTO> = {
  toDomain: therapistDto => ({
    id: therapistDto.id,
    clinicId: therapistDto.organization_id,
    clinicKey: therapistDto.organization_key,
    firstName: decodeHtmlTextFromString(therapistDto.first_name),
    lastName: decodeHtmlTextFromString(therapistDto.last_name),
    contactEmail: therapistDto.contact_email,
    clinicName: decodeHtmlTextFromString(therapistDto.organization_name),
    clinicAddress: nullableDecodeHtmlTextFromString(therapistDto.organization_address),
    profilePictureUrl: therapistDto.profile_picture_url,
    subtitle: therapistDto.subtitle,
    description: nullableDecodeHtmlTextFromString(therapistDto.description),
    preferences: therapistPreferencesMapper.toDomain(therapistDto.preferences),
  }),
  toDTO: therapistDomain => ({
    id: therapistDomain.id,
    organization_id: therapistDomain.clinicId,
    organization_key: therapistDomain.clinicKey,
    first_name: therapistDomain.firstName,
    last_name: therapistDomain.lastName,
    contact_email: therapistDomain.contactEmail,
    organization_name: therapistDomain.clinicName,
    organization_address: therapistDomain.clinicAddress,
    profile_picture_url: therapistDomain.profilePictureUrl,
    subtitle: therapistDomain.subtitle,
    description: therapistDomain.description,
    preferences: therapistPreferencesMapper.toDTO(therapistDomain.preferences),
  }),
};

export const therapistPreferencesMapper = {
  toDomain: (dto: TherapistPreferencesDTO | null): TherapistPreferences => ({
    language: dto?.language || "en-CH",
    notifications: {
      email: {
        newPatientAssigned: dto?.notifications?.email?.new_patient_assigned ?? true,
        openTaskReminders: dto?.notifications?.email?.open_task_reminders ?? true,
        phaseAdvancing: dto?.notifications?.email?.phase_advancing ?? true,
      },
    },
  }),
  toDTO: (domain: TherapistPreferences): TherapistPreferencesDTO => ({
    language: domain.language,
    notifications: {
      email: {
        new_patient_assigned: domain.notifications.email.newPatientAssigned,
        open_task_reminders: domain.notifications.email.openTaskReminders,
        phase_advancing: domain.notifications.email.phaseAdvancing,
      },
    },
  }),
};

export const termsAndConditionsMapper = {
  toDomain: (dto: TermsAndConditionsDTO): TermsAndConditions => ({
    termsAndConditionsAccepted: dto.terms_and_conditions_accepted,
    dataPrivacyPolicyAccepted: dto.data_privacy_policy_accepted,
    termsAndConditionsAcceptedTimestamp: dto.terms_and_conditions_accepted_timestamp,
    dataPrivacyPolicyAcceptedTimestamp: dto.data_privacy_policy_accepted_timestamp,
    termsVersion: dto.terms_version,
    privacyPolicyVersion: dto.privacy_policy_version,
  }),
  toDTO: (domain: TermsAndConditions): TermsAndConditionsDTO => ({
    terms_and_conditions_accepted: domain.termsAndConditionsAccepted ?? false,
    data_privacy_policy_accepted: domain.dataPrivacyPolicyAccepted ?? false,
    terms_and_conditions_accepted_timestamp: domain.termsAndConditionsAcceptedTimestamp ?? new Date().toISOString(),
    data_privacy_policy_accepted_timestamp: domain.dataPrivacyPolicyAcceptedTimestamp ?? new Date().toISOString(),
    terms_version: domain.termsVersion,
    privacy_policy_version: domain.privacyPolicyVersion,
  }),
};
